import { ChangeDetectionStrategy, Component, ViewEncapsulation, computed, contentChildren, effect, inject, input, output } from '@angular/core';
import { DOCUMENT, NgTemplateOutlet } from '@angular/common';
import { DesignSystem } from '@workbuddy/ui/v2/design-system';
import { CheckboxComponent } from '@workbuddy/ui/v2/fields/checkbox';
import { DataTableCellDirective } from './data.table.cell.directive';
import { DataTableColumn } from './data.table.model';
import { dataTableBridgeStyles, dataTableStyleId } from './data.table.theme';

@Component({
    selector: 'w-data-table',
    standalone: true,
    imports: [NgTemplateOutlet, CheckboxComponent],
    templateUrl: './data.table.component.html',
    styleUrls: ['./data.table.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
})
export class DataTableComponent<T = any> {

    private readonly document = inject(DOCUMENT);

    public readonly $rows = input.required<T[]>({ alias: 'rows' });
    public readonly $columns = input.required<DataTableColumn<T>[]>({ alias: 'columns' });
    public readonly $getRowId = input.required<(row: T) => string>({ alias: 'getRowId' });
    public readonly $selectable = input<boolean>(false, { alias: 'selectable' });
    public readonly $selectedIds = input<string[]>([], { alias: 'selectedIds' });
    public readonly $stickyHeader = input<boolean>(true, { alias: 'stickyHeader' });
    /** Min table width — below this the scroll container scrolls horizontally instead of squishing columns. */
    public readonly $minWidth = input<string | null>(null, { alias: 'minWidth' });
    public readonly $frameless = input<boolean>(false, { alias: 'frameless' });
    public readonly $rowClickable = input<boolean>(false, { alias: 'rowClickable' });
    /** When true a click anywhere on the row toggles its selection (checkbox-list UX). When false (default)
     *  only the checkbox toggles and a row click emits `rowClick` — so selection and open-detail can coexist. */
    public readonly $rowClickToggles = input<boolean>(false, { alias: 'rowClickToggles' });
    /** Extra class for a row, e.g. to mark one invalid; the table's own `selected`/`clickable` classes are kept. */
    public readonly $rowClass = input<((row: T) => string | null) | null>(null, { alias: 'rowClass' });

    public readonly rowClick = output<T>();
    public readonly selectedIdsChange = output<string[]>();

    protected readonly $cellTemplates = contentChildren(DataTableCellDirective);

    protected readonly $selectedSet = computed(() => new Set(this.$selectedIds()));
    protected readonly $templateByColumn = computed(() => new Map(this.$cellTemplates().map(t => [t.$column(), t.template])));

    protected readonly $rowClickHandled = computed(() => this.$rowClickToggles() || this.$rowClickable());

    constructor() {

        DesignSystem.Mount(this.document, dataTableStyleId, dataTableBridgeStyles);

        // Column ids drive tracking, cell-template matching and dedup — a duplicate silently collides, so surface it.
        effect(() => {

            const ids = this.$columns().map(column => column.id);
            const duplicate = ids.find((id, index) => ids.indexOf(id) !== index);
            if (duplicate) console.warn(`[w-data-table] duplicate column id "${duplicate}" — cells and templates will collide.`);
        });
    }

    protected rowClassOf(row: T): string | null {

        return this.$rowClass()?.(row) ?? null;
    }

    protected isSelected(row: T): boolean {

        return this.$selectedSet().has(this.$getRowId()(row));
    }

    protected toggleRow(row: T): void {

        const id = this.$getRowId()(row);
        const next = new Set(this.$selectedSet());
        next.has(id) ? next.delete(id) : next.add(id);
        this.selectedIdsChange.emit([...next]);
    }

    protected onRowClick(row: T): void {

        if (this.$rowClickToggles()) {
            this.toggleRow(row);
            return;
        }
        this.rowClick.emit(row);
    }

    protected onRowKeydown(event: KeyboardEvent, row: T): void {

        if (!this.$rowClickHandled()) return;
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        this.onRowClick(row);
    }

    protected cellValue(row: T, column: DataTableColumn<T>): unknown {

        if (column.compute) return column.compute(row);
        return column.accessor ? row[column.accessor] : undefined;
    }
}
